import type { RowData, TableFeatures, TableOptions, ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { useTable } from "@tanstack/react-table";

import { useEffect, useState } from "react";
import { TableHeader } from "./tableHeader";
import { TableFooter } from "./tableFooter";
import { DataTable } from "./datatable";
import { DEFAULT_PAGINATION, type CommonFeatures } from "@/hooks/useTanstackTable";

interface TypedTableOptions<TF extends TableFeatures, TD extends RowData> {
	readonly features: TF;
	readonly columns: Array<ColumnDef<TF, TD>>;
	readonly data: TD[];

	readonly pagination?: true;
	readonly globalSearch?: true;

	readonly className?: string,
	readonly rowClassName?: (data: TD) => string,
	readonly getRowId?: (data: TD) => string,
	readonly onRowClick?: (e: React.MouseEvent, data: TD) => void,
	// scrollY?: string,

	// readonly selectionAtom?: Atom<RowSelectionState>;
	readonly selection?: RowSelectionState;
	readonly setSelection?: Setter<RowSelectionState>;

	readonly noResultsMessage: string;
	readonly errorMessage: string;
	readonly isLoading: boolean;
	readonly isError: boolean;
}
export function TypedTable<TF extends CommonFeatures, TD extends RowData>(props: TypedTableOptions<TF, TD>) {
	const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
	const [globalFilter, setGlobalFilter] = useState("");
	// const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

	const table = useTable({
		features: props.features,
		columns: props.columns,
		data: props.data,

		getRowId: props.getRowId,

		state: {
			pagination: props.pagination == true ? pagination : { pageSize: Infinity, pageIndex: 0 },
			globalFilter: props.globalSearch == true ? globalFilter : undefined,
			rowSelection: props.selection
		},
		onRowSelectionChange: props.setSelection,

		// V9 recomienda usar Atoms, pero la documentacion dice que use useCreateAtom, que no existe en la version actual
		// Store está en V0 así que, por ahora, se queda así.
		// atoms: {
		// 	rowSelection: props.selectionAtom, // selection APIs now update rowSelectionAtom
		// },
	} as unknown as TableOptions<TF, TD>);

	useEffect(() => {
		setPagination({ pageIndex: 0, pageSize: pagination.pageSize })
	}, [props.data]);

	return (
		<div>
			<TableHeader
				pagination={props.pagination == true ? pagination : undefined}
				setPagination={setPagination}
				globalSearch={props.globalSearch == true ? globalFilter : undefined}
				setGlobalSearch={setGlobalFilter}
			/>

			<DataTable
				table={table}
				className={props.className}
				rowClassName={props.rowClassName}
				onRowClick={props.onRowClick}

				noResultsMessage={props.noResultsMessage}
				errorMessage={props.errorMessage}
				isLoading={props.isLoading}
				isError={props.isError}
			/>

			{props.pagination == true && (
				<TableFooter
					table={table}
					pagination={pagination}
					setPagination={setPagination}
				/>
			)}
		</div>
	);
}
